import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import { useAuth } from '../context/AuthContext';
import { getPackableAppointments, getInventoryForAppointment, createPack, restockAll } from '../api/dispatch';
import { getSizes, getCategories, getUseCases } from '../api/meta';

const GENDERS = ['girl', 'boy', 'unisex'];

function genderShort(g) {
  const k = (g || '').toLowerCase();
  return k === 'boy' ? 'B' : k === 'girl' ? 'G' : k === 'unisex' ? 'U' : (g || '');
}

// Which of allUseCases appear in an item's use_case_tags string (dash-insensitive).
function matchUseCases(tagStr, allUseCases) {
  if (!tagStr) return [];
  const norm = String(tagStr).toLowerCase().replace(/-/g, '');
  return allUseCases.filter(u => norm.includes(u.toLowerCase().replace(/-/g, '')));
}

// Customer-status tag colours: confirmed = green, message sent = blue,
// cancelled = red (can still be operationally 'scheduled'), anything else grey.
const CUSTOMER_STATUS_STYLE = {
  confirmed_on_call_or_text: 'bg-green-100 text-green-800',
  confirmed_on_delivery_day: 'bg-green-100 text-green-800',
  message_sent: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-700',
};
const statusLabel = (s) => {
  const t = String(s || '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// Slot start -> epoch ms. Copied from Appointments.jsx (kept local to avoid a
// shared-util refactor); handles the "11:00 PM - 12:00 PM means 11 AM" glitch.
function parseSlotStart(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const parts = String(timeStr).split(/[-–]/);
  const startStr = parts[0].trim();
  const endStr = (parts[1] || '').trim();
  const m = startStr.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10); const min = parseInt(m[2] || '0', 10);
  let period = (m[3] || '').toUpperCase();
  if (h === 11 && period === 'PM' && /12(:00)?\s*PM/i.test(endStr)) period = 'AM';
  if (period === 'PM' && h !== 12) h += 12;
  else if (period === 'AM' && h === 12) h = 0;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  d.setHours(h, min, 0, 0);
  return d.getTime();
}

// ── Filter modal: size + gender (server fetch keys) + category + use-case ─────
function FilterModal({ allSizes, allCategories, allUseCases, sizes, genders, categories, useCases, onApply, onClose }) {
  const [selSizes, setSelSizes] = useState(new Set(sizes));
  const [selGenders, setSelGenders] = useState(new Set(genders));
  const [selCategories, setSelCategories] = useState(new Set(categories));
  const [selUseCases, setSelUseCases] = useState(new Set(useCases));
  const toggle = (set, val, setter) => {
    const n = new Set(set); n.has(val) ? n.delete(val) : n.add(val); setter(n);
  };
  const chip = (val, on, onClick, label) => (
    <button key={val} onClick={onClick}
      className={`text-sm font-semibold py-2 px-2 rounded-lg border capitalize ${on ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
      {label || val}
    </button>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={onClose}>
      <div className="bg-white w-full rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-bold text-gray-800 mb-3">Filter items</h3>

        <p className="text-xs font-semibold text-gray-500 mb-1.5">Gender</p>
        <div className="flex gap-2 mb-4">
          {GENDERS.map(g => chip(g, selGenders.has(g), () => toggle(selGenders, g, setSelGenders)))}
        </div>

        <p className="text-xs font-semibold text-gray-500 mb-1.5">Size</p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {allSizes.map(s => chip(s, selSizes.has(s), () => toggle(selSizes, s, setSelSizes)))}
        </div>

        <p className="text-xs font-semibold text-gray-500 mb-1.5">Category</p>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {allCategories.map(c => chip(c, selCategories.has(c), () => toggle(selCategories, c, setSelCategories)))}
        </div>

        <p className="text-xs font-semibold text-gray-500 mb-1.5">Use case</p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {allUseCases.map(u => chip(u, selUseCases.has(u), () => toggle(selUseCases, u, setSelUseCases)))}
        </div>

        <div className="flex gap-3">
          <button onClick={() => onApply(Array.from(selSizes), Array.from(selGenders), Array.from(selCategories), Array.from(selUseCases))}
            disabled={selSizes.size === 0 || selGenders.size === 0}
            className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-3 rounded-lg disabled:opacity-50">Apply</button>
          <button onClick={onClose} className="border border-gray-200 text-gray-600 text-sm font-semibold px-6 rounded-lg">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Pack view: big thumbnails, tap-to-add, rating, Pack ───────────────────────
function PackView({ appt, allSizes, allCategories, allUseCases, onDone, onBack }) {
  const children = appt.children || [];
  const [childIdx, setChildIdx] = useState(0);
  const child = children[childIdx] || {};

  const [sizes, setSizes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [categories, setCategories] = useState([]);   // client filter; [] until options load = all
  const [useCases, setUseCases] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [selMeta, setSelMeta] = useState({});
  const [rating, setRating] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [showBasket, setShowBasket] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [packing, setPacking] = useState(false);

  // Default category/use-case filters to "all" once the option lists load.
  useEffect(() => { setCategories(allCategories); }, [allCategories]);
  useEffect(() => { setUseCases(allUseCases); }, [allUseCases]);

  // Default fetch keys from the active child (its size; its gender + unisex).
  useEffect(() => {
    setSizes(child.size ? [child.size] : []);
    setGenders(child.gender ? [child.gender, 'unisex'] : ['unisex']);
  }, [childIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadItems = useCallback(async () => {
    if (sizes.length === 0 || genders.length === 0) { setItems([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      setItems(await getInventoryForAppointment(appt.appointment_id, { sizes, genders }));
    } catch (e) { setError('Failed to load inventory'); }
    finally { setLoading(false); }
  }, [appt.appointment_id, sizes, genders]);

  useEffect(() => { loadItems(); }, [loadItems]);

  function toggle(item) {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(item.item_id) ? n.delete(item.item_id) : n.add(item.item_id);
      return n;
    });
    setSelMeta(prev => ({ ...prev, [item.item_id]: item }));
  }

  const basket = useMemo(
    () => Array.from(selected).map(id => selMeta[id]).filter(Boolean)
      .sort((a, b) => (a.zoddle_barcode || '').localeCompare(b.zoddle_barcode || '')),
    [selected, selMeta]
  );

  // Client-side category + use-case filter (mirrors the desktop pack modal).
  const catSet = useMemo(() => new Set(categories), [categories]);
  const ucSet = useMemo(() => new Set(useCases), [useCases]);
  const allCatsSel = allCategories.length > 0 && allCategories.every(c => catSet.has(c));
  const allUcsSel = allUseCases.length > 0 && allUseCases.every(u => ucSet.has(u));
  const visible = useMemo(() => items.filter(it => {
    if (it.category) { if (!catSet.has(it.category)) return false; }
    else if (!allCatsSel) return false;
    const ucs = matchUseCases(it.use_case_tags, allUseCases);
    if (ucs.length) { if (!ucs.some(u => ucSet.has(u))) return false; }
    else if (!allUcsSel) return false;
    return true;
  }), [items, catSet, ucSet, allCatsSel, allUcsSel, allUseCases]);

  async function handlePack() {
    if (selected.size === 0) return;
    if (rating == null && !window.confirm('Submit this pack without rating it out of 5?')) return;
    setPacking(true);
    try {
      await createPack(appt.appointment_id, Array.from(selected), rating);
      onDone(`Packed ${selected.size} item${selected.size !== 1 ? 's' : ''} for ${appt.customer_name || 'order'}`);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to pack'); setPacking(false); }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 z-30">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="text-sm text-gray-400">← Back</button>
          <button onClick={() => setShowFilter(true)} className="flex items-center gap-1 text-sm font-semibold text-zoddle-green">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M3 4h18M6 12h12M10 20h4" />
            </svg>
            Filter
          </button>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-base font-bold text-gray-800">{appt.customer_name || '—'}</p>
          <button onClick={() => setShowInfo(true)} aria-label="Preferences"
            className="w-5 h-5 rounded-full border border-gray-300 text-gray-400 text-xs font-bold flex items-center justify-center shrink-0">i</button>
        </div>
        <p className="text-xs text-gray-400">{appt.scheduled_time} · UID <span className="font-mono">{appt.zcp_appointment_uid || '—'}</span> · showing {sizes.join(',')} · {genders.map(genderShort).join('/')}</p>
        {children.length > 1 && (
          <div className="flex gap-2 mt-2">
            {children.map((c, i) => (
              <button key={i} onClick={() => setChildIdx(i)}
                className={`text-xs font-semibold px-3 py-1 rounded-full border ${i === childIdx ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
                {c.size} {genderShort(c.gender)}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="m-4 bg-pink-50 border border-pink-200 text-zoddle-pink text-sm rounded-lg px-3 py-2">{error}</div>}

      {loading ? <div className="text-center py-20 text-gray-400 text-sm">Loading…</div>
      : visible.length === 0 ? <div className="text-center py-20 text-gray-400 text-sm">No packable items for this filter.</div>
      : (
        <div className="grid grid-cols-2 gap-3 p-4">
          {visible.map(it => {
            const on = selected.has(it.item_id);
            return (
              <div key={it.item_id} onClick={() => toggle(it)}
                className={`border-2 rounded-xl p-2 transition ${on ? 'border-zoddle-green bg-green-50' : 'border-gray-200 bg-white'}`}>
                {it.image_url_1
                  ? <img src={cldThumb(it.image_url_1, 600)} alt={it.design_name} className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                  : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                <p className="text-xs font-semibold text-gray-800 truncate">{it.brand_name || '—'}</p>
                <p className="text-xs text-gray-500 truncate">{it.size} · {genderShort(it.gender)} · <span className="font-bold text-zoddle-green">₹{it.zoddle_price}</span></p>
                <p className="text-xs font-mono text-gray-400 truncate">{it.zoddle_barcode}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky bottom bar: rating + pack */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-gray-500 mr-1">Rate:</span>
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => setRating(r => r === n ? null : n)}
                className={`text-xl leading-none ${rating && n <= rating ? 'text-yellow-400' : 'text-gray-300'}`}>★</button>
            ))}
          </div>
          <button onClick={() => setShowBasket(true)} disabled={selected.size === 0}
            className="text-xs font-semibold text-zoddle-blue disabled:text-gray-300">Basket ({selected.size})</button>
        </div>
        <button onClick={handlePack} disabled={packing || selected.size === 0}
          className="w-full bg-zoddle-green text-white text-sm font-bold py-3 rounded-lg disabled:opacity-50">
          {packing ? 'Packing…' : `Pack ${selected.size} item${selected.size !== 1 ? 's' : ''}`}
        </button>
      </div>

      {showFilter && (
        <FilterModal allSizes={allSizes} allCategories={allCategories} allUseCases={allUseCases}
          sizes={sizes} genders={genders} categories={categories} useCases={useCases}
          onApply={(s, g, c, u) => { setSizes(s); setGenders(g); setCategories(c); setUseCases(u); setShowFilter(false); }}
          onClose={() => setShowFilter(false)} />
      )}
      {showInfo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setShowInfo(false)}>
          <div className="bg-white w-full rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-800 mb-1">Preferences</h3>
            <p className="text-xs text-gray-400 mb-3">{child.size} · <span className="capitalize">{child.gender}</span></p>
            <div className="space-y-2 text-sm">
              {[['Brand', child.brand_pref], ['Use case', child.use_case_pref],
                ['Style', child.style_pref], ['Avoid', child.avoid_tags]].map(([label, val]) => (
                <div key={label} className="flex gap-2">
                  <span className="text-gray-400 w-20 shrink-0">{label}</span>
                  <span className="text-gray-800">{val || <span className="text-gray-300">—</span>}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowInfo(false)} className="mt-4 w-full border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg">Done</button>
          </div>
        </div>
      )}
      {showBasket && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setShowBasket(false)}>
          <div className="bg-white w-full rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-800 mb-3">Basket · {basket.length}</h3>
            <div className="space-y-2">
              {basket.map(it => (
                <div key={it.item_id} className="flex items-center gap-2 border border-gray-100 rounded-lg p-2">
                  {it.image_url_1 && <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-10 h-10 rounded object-cover bg-gray-100" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{it.brand_name} · {it.size}</p>
                    <p className="text-xs font-mono text-gray-400 truncate">{it.zoddle_barcode}</p>
                  </div>
                  <button onClick={() => toggle(it)} className="text-zoddle-pink font-bold px-2">×</button>
                </div>
              ))}
            </div>
            <button onClick={() => setShowBasket(false)} className="mt-4 w-full border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── List view: appointments to pack in the next 36h ───────────────────────────
export default function MobilePackApp() {
  const { auth, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [allSizes, setAllSizes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allUseCases, setAllUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState(null);
  const [restocking, setRestocking] = useState(false);

  async function handleRestockAll() {
    if (!window.confirm('Send all reversed items to their suggested bins? Items with no available bin will be skipped.')) return;
    setRestocking(true);
    try {
      const res = await restockAll();
      setMsg(`Restocked ${res.restocked} item(s).` + (res.skipped ? ` ${res.skipped} skipped (no available bin).` : ''));
    } catch (e) { setMsg(e.response?.data?.detail || 'Restock all failed'); }
    finally { setRestocking(false); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPackableAppointments();
      const horizon = Date.now() + 36 * 3600 * 1000;
      const within = data
        .map(a => ({ a, start: parseSlotStart(a.scheduled_date, a.scheduled_time) }))
        .filter(x => x.start != null && x.start <= horizon)
        .sort((x, y) => x.start - y.start)
        .map(x => x.a);
      setAppts(within);
    } catch (e) { /* leave empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getSizes().then(setAllSizes).catch(() => {});
    getCategories().then(setAllCategories).catch(() => {});
    getUseCases().then(setAllUseCases).catch(() => {});
  }, []);

  if (active) {
    return <PackView appt={active} allSizes={allSizes} allCategories={allCategories} allUseCases={allUseCases}
      onBack={() => setActive(null)}
      onDone={(text) => { setActive(null); setMsg(text); load(); }} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <h1 className="text-zoddle-green font-black text-lg">ZOCS</h1>
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">Pack</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-700 text-sm font-medium truncate max-w-[120px]">{auth.full_name}</span>
          <button onClick={logout} className="text-sm text-gray-400">Sign out</button>
        </div>
      </nav>

      <main className="p-4 max-w-xl mx-auto">
        {msg && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-3 py-2 mb-4">{msg}</div>}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800">To pack · next 36h</h2>
          <div className="flex items-center gap-3">
            <button onClick={handleRestockAll} disabled={restocking}
              className="bg-zoddle-green text-white text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
              {restocking ? 'Restocking…' : 'Restock all'}
            </button>
            <button onClick={load} className="text-xs text-gray-400">↻ Refresh</button>
          </div>
        </div>

        {loading ? <div className="text-center py-20 text-gray-400 text-sm">Loading…</div>
        : appts.length === 0 ? <div className="text-center py-20 text-gray-400 text-sm">Nothing to pack in the next 36 hours.</div>
        : (
          <div className="space-y-2">
            {appts.map(a => (
              <div key={a.appointment_id} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800">{a.customer_name || '—'}</p>
                    <p className="text-xs text-gray-400">
                      {a.scheduled_time || '—'} · UID <span className="font-mono">{a.zcp_appointment_uid || '—'}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {(a.children || []).map(c => `${c.size} ${genderShort(c.gender)}`).join(', ') || '—'}
                    </p>
                    <div className="flex flex-wrap items-center gap-1 mt-1 text-[10px] leading-tight">
                      {a.customer_status && (
                        <span className={`px-1.5 py-0.5 rounded font-medium ${CUSTOMER_STATUS_STYLE[a.customer_status] || 'bg-gray-100 text-gray-600'}`}>
                          {statusLabel(a.customer_status)}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{statusLabel(a.operational_status)}</span>
                      {a.booked_on && <span className="text-gray-400 italic">booked {a.booked_on.slice(8, 10)}/{a.booked_on.slice(5, 7)}</span>}
                    </div>
                  </div>
                  <button onClick={() => setActive(a)}
                    className="bg-zoddle-green text-white text-sm font-bold px-5 py-2 rounded-lg shrink-0">Pack</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
